import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { Btn, Card, F, PageHeader, Toggle, num } from "../../components/admin/ui";
import { ImagesEditor } from "../../components/admin/ImagesEditor";
import { VariantsEditor, buildVariants } from "../../components/admin/VariantsEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EMPTY = {
  name_ar: "", name_en: "", slug: "", sku: "", description_ar: "", description_en: "", fabric_ar: "", fabric_en: "", care_ar: "", care_en: "",
  images: [], price: "", sale_price: "", category_id: "", status: "draft", is_new: false, is_bestseller: false, is_featured: false, is_offer: false,
  shipping_rule: "zone", shipping_fee: 0, cost_price: "", sizes: [], colors: [], variants: [{ size: "", color: "", stock: 0, sku: "" }], show_size_chart: true,
};

const Sel = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1.5">
    <p className="text-xs text-gray-600">{label}</p>
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="bg-white"><SelectValue /></SelectTrigger>
      <SelectContent dir="rtl">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export default function ProductForm() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const bind = (k) => ({ value: form[k], onChange: (v) => set({ [k]: v }) });

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCats(r.data));
    if (!isNew) api.get(`/admin/products/${id}`).then((r) => setForm({ ...EMPTY, ...r.data, sale_price: r.data.sale_price ?? "" }));
  }, [id, isNew]);

  const save = async () => {
    setBusy(true);
    const body = {
      ...form, price: num(form.price), cost_price: num(form.cost_price), sale_price: form.sale_price === "" ? null : num(form.sale_price), shipping_fee: num(form.shipping_fee),
      category_id: form.category_id || null, colors: form.colors.filter((c) => c.name_ar),
      variants: form.variants.length ? form.variants : buildVariants([], []),
    };
    delete body.sizesText;
    try {
      if (isNew) await api.post("/admin/products", body);
      else await api.put(`/admin/products/${id}`, body);
      toast.success("تم حفظ المنتج");
      nav("/admin/products");
    } catch (e) {
      const m = { sku_exists: "رمز SKU مستخدم مسبقًا", sale_price_must_be_lower: "سعر التخفيض يجب أن يكون أقل من السعر الأصلي" };
      toast.error(m[e?.response?.data?.detail] || errText(e, "تحقق من الحقول المطلوبة"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-product-form">
      <PageHeader title={isNew ? "إضافة منتج" : "تعديل المنتج"}>
        <Btn variant="light" onClick={() => nav("/admin/products")}>إلغاء</Btn>
        <Btn data-testid="save-product-button" onClick={save} disabled={busy || !form.name_ar || !form.sku || !form.price}>حفظ</Btn>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="المعلومات الأساسية">
            <div className="grid gap-4 sm:grid-cols-2">
              <F label="اسم المنتج بالعربية *" testid="product-name-ar-input" {...bind("name_ar")} />
              <F label="الاسم بالإنجليزية (اختياري)" dir="ltr" testid="product-name-en-input" {...bind("name_en")} />
              <F label="رمز المنتج SKU *" dir="ltr" testid="product-sku-input" {...bind("sku")} />
              <F label="الرابط المختصر (slug)" dir="ltr" {...bind("slug")} />
              <F label="الوصف بالعربية" textarea className="sm:col-span-2" testid="product-desc-ar-input" {...bind("description_ar")} />
              <F label="Description (EN)" textarea dir="ltr" className="sm:col-span-2" {...bind("description_en")} />
              <F label="الخامة" {...bind("fabric_ar")} />
              <F label="Fabric (EN)" dir="ltr" {...bind("fabric_en")} />
              <F label="تعليمات العناية" {...bind("care_ar")} />
              <F label="Care (EN)" dir="ltr" {...bind("care_en")} />
            </div>
          </Card>
          <Card title="الصور"><ImagesEditor images={form.images} onChange={(images) => set({ images })} /></Card>
          <Card title="المقاسات والألوان والمخزون"><VariantsEditor form={form} set={set} /></Card>
        </div>
        <div className="space-y-4">
          <Card title="الحالة والتصنيف">
            <div className="space-y-4">
              <Sel label="حالة المنتج" testid="product-status-select" value={form.status} onChange={(v) => set({ status: v || "draft" })} options={[["published", "منشور"], ["draft", "مسودة"], ["unavailable", "غير متوفر"]]} />
              <Sel label="التصنيف" testid="product-category-select" value={form.category_id} onChange={(v) => set({ category_id: v })} options={[["none", "بدون تصنيف"], ...cats.map((c) => [c.id, c.name_ar])]} />
            </div>
          </Card>
          <Card title="السعر (ر.س، شامل الضريبة)">
            <div className="grid grid-cols-2 gap-3">
              <F label="السعر الأصلي *" type="number" testid="product-price-input" {...bind("price")} />
              <F label="سعر التخفيض" type="number" testid="product-sale-price-input" {...bind("sale_price")} />
              <F label="تكلفة القطعة (لحساب الأرباح)" type="number" testid="product-cost-input" className="col-span-2" {...bind("cost_price")} />
              {num(form.price) > 0 && num(form.cost_price) > 0 && (() => {
                const sell = num(form.sale_price) || num(form.price);
                const profit = sell / 1.15 - num(form.cost_price);
                return <p data-testid="product-profit-preview" className={`col-span-2 text-xs ${profit < 0 ? "text-red-600" : "text-green-700"}`}>صافي الربح للقطعة (بدون الضريبة): {profit.toFixed(2)} ر.س</p>;
              })()}
              <a href="/admin/calculator" target="_blank" rel="noreferrer" className="col-span-2 text-xs text-gold-deep underline">افتحي حاسبة التسعير</a>
            </div>
          </Card>
          <Card title="الظهور في الأقسام">
            <div className="space-y-2">
              <Toggle label="جديد" testid="flag-new" checked={form.is_new} onChange={(v) => set({ is_new: v })} />
              <Toggle label="الأكثر مبيعًا" testid="flag-bestseller" checked={form.is_bestseller} onChange={(v) => set({ is_bestseller: v })} />
              <Toggle label="مميز" testid="flag-featured" checked={form.is_featured} onChange={(v) => set({ is_featured: v })} />
              <Toggle label="ضمن العروض" testid="flag-offer" checked={form.is_offer} onChange={(v) => set({ is_offer: v })} />
              <Toggle label="إظهار جدول المقاسات" checked={form.show_size_chart} onChange={(v) => set({ show_size_chart: v })} />
            </div>
          </Card>
          <Card title="الشحن">
            <div className="space-y-3">
              <Sel label="قاعدة الشحن" testid="product-shipping-rule-select" value={form.shipping_rule} onChange={(v) => set({ shipping_rule: v || "zone" })} options={[["zone", "حسب منطقة التوصيل"], ["free", "شحن مجاني"], ["fixed", "تكلفة ثابتة"]]} />
              {form.shipping_rule === "fixed" && <F label="تكلفة الشحن (ر.س)" type="number" {...bind("shipping_fee")} />}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
